import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import biddingTableConfig from './bidding-table-config.json';
import { getRiskLevel, formatCurrency, formatPercentage } from './bidding-table-utils';
import './optimal-bidding-table.css';

/**
 * Optimal Bidding Table Component
 * Displays optimal bidding strategies with risk analysis
 */
const OptimalBiddingTable = ({ 
  selectedRisk,
  data,
  isLoading = false,
  error = null,
  onRetry = null,
  className = "",
  onRowClick = null,
  onDataUpdate = null
}) => {
  // Use local configuration
  const config = biddingTableConfig;

  // Get risk level based on thresholds
  const getRiskLevel = (row, thresholds) => {
    const profit = parseFloat(row.expectedProfit) || 0;
    
    if (!thresholds) return 'medium'; // Default if thresholds not available
    
    if (profit >= thresholds.high) return 'low';
    if (profit >= thresholds.medium) return 'medium';
    return 'high';
  };

  // Process table data with risk highlighting
  const processedData = useMemo(() => {
    if (!data) return [];

    return data.map((row, index) => ({
      ...row,
      id: row.id || index,
      riskLevel: getRiskLevel(row, config?.riskThresholds),
      isHighlighted: selectedRisk === 'All' || row.category === selectedRisk
    }));
  }, [data, selectedRisk, config?.riskThresholds]);

  // Get value class for styling
  const getValueClass = (value, type = 'neutral') => {
    const numValue = parseFloat(value) || 0;
    
    if (type === 'profit' || type === 'percentage') {
      if (numValue > 0) return 'positive';
      if (numValue < 0) return 'negative';
    }
    
    return 'neutral';
  };

  // Format currency values
  const formatCurrency = (value) => {
    const num = parseFloat(value) || 0;
    return `$${num.toLocaleString(undefined, { 
      minimumFractionDigits: 0, 
      maximumFractionDigits: 0 
    })}`;
  };

  // Format percentage values
  const formatPercentage = (value) => {
    const num = parseFloat(value) || 0;
    return `${num.toFixed(1)}%`;
  };

  // Handle row click
  const handleRowClick = (row) => {
    if (onRowClick) {
      onRowClick(row);
    }
  };

  // Notify parent when data updates
  React.useEffect(() => {
    if (onDataUpdate && processedData.length > 0) {
      onDataUpdate(processedData);
    }
  }, [processedData, onDataUpdate]);

  // Render loading state
  if (isLoading) {
    return (
      <div className="optimal-bidding-loading">
        <div className="optimal-bidding-loading-spinner">⟳</div>
        Loading optimal bidding data...
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div className="optimal-bidding-error">
        <div className="optimal-bidding-error-message">
          {error.message || 'Failed to load optimal bidding table data'}
        </div>
        {error.canRetry && onRetry && (
          <button 
            className="optimal-bidding-retry-button" 
            onClick={onRetry}
            aria-label="Retry loading optimal bidding data"
          >
            Retry
          </button>
        )}
      </div>
    );
  }

  // Render no data state
  if (!processedData || processedData.length === 0) {
    return (
      <div className="optimal-bidding-error">
        <div className="optimal-bidding-error-message">
          No optimal bidding data available
        </div>
      </div>
    );
  }

  return (
    <div className={`optimal-bidding-table-container ${className}`}>
      <div className="optimal-bidding-table-wrapper">
        <table 
          className="optimal-bidding-table"
          role="table"
          aria-label="Optimal bidding strategies table"
        >
          <thead>
            <tr>
              {(config?.columns || []).map((column) => (
                <th key={column.key} scope="col">
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {processedData.map((row) => (
              <tr 
                key={row.id}
                className={`${row.riskLevel ? `optimal-bidding-risk-${row.riskLevel}` : ''} ${
                  row.isHighlighted ? 'highlighted-row' : ''
                }`}
                onClick={() => handleRowClick(row)}
                style={{ 
                  cursor: onRowClick ? 'pointer' : 'default',
                  opacity: row.isHighlighted ? 1 : 0.8
                }}
                role="row"
                tabIndex={onRowClick ? 0 : undefined}
                onKeyDown={(e) => {
                  if (onRowClick && (e.key === 'Enter' || e.key === ' ')) {
                    e.preventDefault();
                    handleRowClick(row);
                  }
                }}
              >
                {(config?.columns || []).map((column) => (
                  <td 
                    key={`${row.id}-${column.key}`}
                    className={column.type ? `optimal-bidding-${getValueClass(row[column.key], column.type)}` : ''}
                    role="cell"
                  >
                    {column.key === 'status' ? (
                      <span className={`optimal-bidding-status optimal-bidding-status-${row[column.key]?.toLowerCase() || 'inactive'}`}>
                        {row[column.key] || 'Inactive'}
                      </span>
                    ) : column.type === 'currency' ? (
                      formatCurrency(row[column.key])
                    ) : column.type === 'percentage' ? (
                      formatPercentage(row[column.key])
                    ) : (
                      row[column.key] || '-'
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// PropTypes for development
OptimalBiddingTable.propTypes = {
  selectedRisk: PropTypes.string.isRequired,
  data: PropTypes.array,
  isLoading: PropTypes.bool,
  error: PropTypes.object,
  onRetry: PropTypes.func,
  className: PropTypes.string,
  onRowClick: PropTypes.func,
  onDataUpdate: PropTypes.func
};

// Default props
OptimalBiddingTable.defaultProps = {
  data: null,
  isLoading: false,
  error: null,
  onRetry: null,
  className: "",
  onRowClick: null,
  onDataUpdate: null
};

export default React.memo(OptimalBiddingTable);
